import React, { Component } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep'
import { sharedConstants } from 'shared-helpers';

// constants
import { ALLOWED_QUERY_OPERATOR, DATA_SET_TYPES, FUNDING_STAGES, FUNDING_STATUS } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';

// helpers
import { prepareFilterParam } from '/src/lib/queryParams';

// apis
import userApi from '/src/apis/userApi';
import exportRequestApi from '/src/apis/exportRequestApi';

// modal open/close, decoupled from the Export click via modalController
import { registerModal, unregisterModal } from '../modalController';
import { lockBodyScroll, unlockBodyScroll } from './modalBehavior';

// components
import DataExportModal from './DataExportModal';

// react select styles
const reactSelectSmall = sharedConstants.reactSelectSmall;
const reactSelectTheme = sharedConstants.reactSelectTheme;

// A user with the directory (or the limited directory) subscription has a data export quota to read
export const hasDataExportAccess = (user) => {
    const userRoles = user.roles || [];

    return userRoles.includes(MAGNITT_ACCESS_ROLES.directory_access) || userRoles.includes(MAGNITT_ACCESS_ROLES.limited_directory_access);
};

// the directories whose export is of the rows they list (the selected ones, or the filtered ones), with no default filters of their own
// the directories whose views export the rows ticked on the page by their id (an `id` filter), when rows are ticked
const ENTITY_DIRECTORIES = ['Startup', 'Ecosystem', 'Company', 'Lps', 'InvestorFunds', 'InvestorInvestments'];



export class DataExportPopup extends Component {

    constructor(props) {
        super(props);
        this.state = {
            // modal open/close (see openModal/handleClosePopup)
            isOpen: false,
            isVisible: false,

            // user quota info
            period: '',
            daysUntilReset: '',
            dataExportQuota: {},
            isCustomDataExport: false,

            // form fields
            numberOfRecords: 0,
            sort: "",

            // request details
            selectedFields: [],
            selectedFilters: "",
            selectedSort: "",

            // options
            isProcessing: false,
            isSubscribed: false,
            errorMessage: "",
            errorFields: "",

            selectedFundingFields: [{
                fieldName: (this.props.dataSetType == DATA_SET_TYPES.privateEquity) ? "Transactions"
                    : (this.props.dataSetType == DATA_SET_TYPES.company) ? "Transactions and Exits"
                        : "Funding and Exits",
                keyName: 'funding_data',
                checked: false,
                disabled: false,
                allowAccess: true
            }],
            selectAll: false,

            // funding rounds quota in startup directory
            fundingDataExportQuota: [],
            showFundingQuota: false,
            fundingCountProcessing: false,
            fundingRoundCount: 0,
            fundingMessage: '',

            // private equity data export
            isGeneralQuota: false
        }
        this.isComponentMounted = false;
    }

    componentDidMount = () => {
        this.isComponentMounted = true;
        const { user, embedded, quotaRequest } = this.props;

        // get user quota if user is subscribed
        if (hasDataExportAccess(user)) {
            // the user's data export quota: an embedded popup reads its parent's request (one for all the views), now or when it is made (componentDidUpdate)
            if (!embedded) {
                this.getUserDataExportQuota();
            } else if (quotaRequest) {
                this.applyDataExportQuota(quotaRequest);
            }

            // the fields and the default sort
            this.updateState({
                selectedFields: this.prepSelectedFields(),
                sort: this.getDefaultSort(this.props.sort)
            });
        }

        // an embedded popup is one view of a multi-view export (see MultiViewDataExportPopup): the modal, its open/close and its Send button are the parent's
        if (!this.props.embedded) {
            registerModal('dataExportModal', this.openModal);
            document.addEventListener('keydown', this.handleKeyDown);
        }
        this.reportSubmitMode();
    }

    componentDidUpdate = (prevProps) => {
        const { user, embedded, quotaRequest } = this.props;

        // the parent asked for the quota again (it is mounted, or a request was sent)
        if (embedded && quotaRequest && quotaRequest !== prevProps.quotaRequest && hasDataExportAccess(user)) {
            this.applyDataExportQuota(quotaRequest);
        }

        this.reportSubmitMode();
    }

    componentWillUnmount = () => {
        this.isComponentMounted = false;
        if (!this.props.embedded) {
            unregisterModal('dataExportModal');
            document.removeEventListener('keydown', this.handleKeyDown);
            if (this.state.isOpen) {
                unlockBodyScroll();
            }
        }
    }

    // An embedded popup tells its parent when what its Send button would be changes (the quota arrived, the records count changed, ...)
    reportSubmitMode = () => {
        const { embedded, onSubmitModeChange } = this.props;

        if (!embedded || !onSubmitModeChange) {
            return;
        }

        const mode = this.getSubmitMode();
        if (mode !== this.lastSubmitMode) {
            this.lastSubmitMode = mode;
            onSubmitModeChange(mode);
        }
    }

    // What an embedded popup starts with each time its parent is opened (see getOpenState)
    prepareForOpen = () => {
        this.updateState(this.getOpenState());
    }

    // What the form starts with each time it is opened: the table's sort in force, the most records the quota and the results allow, and no
    // message from an earlier request (an export clears the sort and counts the quota again, which left the form empty or off for the next one)
    getOpenState = () => {
        const { recordsCount } = this.props;
        const { dataExportQuota } = this.state;
        const state = { sort: this.getDefaultSort(this.props.sort), errorMessage: '', errorFields: '' };

        if (dataExportQuota) {
            state.numberOfRecords = Math.min(dataExportQuota.number_of_records ?? recordsCount, recordsCount);
        }

        return state;
    }

    openModal = () => {
        lockBodyScroll();
        this.updateState({ isOpen: true, ...this.getOpenState() }, () => {
            requestAnimationFrame(() => this.updateState({ isVisible: true }));
        });
    }

    handleKeyDown = (e) => {
        if (e.key === 'Escape' && this.state.isOpen) {
            this.handleClosePopup();
        }
    }

    componentWillReceiveProps = (nextProps, nextState) => {
        if (nextProps.sort != this.props.sort) {
            // set the default sort
            const selectedSort = this.getDefaultSort(nextProps.sort);

            this.updateState({
                sort: selectedSort
            });
        }

        if (this.state.numberOfRecords != '' && nextProps.recordsCount != this.props.recordsCount) {
            // resync to the quota-capped filtered count on every filter change
            this.updateState({
                numberOfRecords: Math.min(this.state.dataExportQuota?.number_of_records ?? nextProps.recordsCount, nextProps.recordsCount)
            });
        }

        // isSubscribedUser can resolve after prepSelectedFields() already ran - unlock paid fields that were computed as locked too early
        if (nextProps.isSubscribedUser && nextProps.isSubscribedUser !== this.props.isSubscribedUser) {
            const selectedFields = (this.state.selectedFields || []).map((field) =>
                (!field.freeAccess && !field.specialAccess && !field.default)
                    ? { ...field, allowAccess: true, disabled: false }
                    : field
            );
            this.updateState({ selectedFields });
        }

        // selectableFields can grow after mount too (e.g. DFHQ-only columns) - merge in new fields without disturbing existing toggles
        if (this.state.selectedFields && this.state.selectedFields.length && nextProps.selectableFields) {
            const currentKeyNames = new Set(this.state.selectedFields.map((field) => field.keyName));
            const newFields = nextProps.selectableFields.filter((field) => !currentKeyNames.has(field.keyName));

            if (newFields.length) {
                const preparedNewFields = cloneDeep(newFields).map((field) => this.prepFieldAccess(field, nextProps.user, nextProps.isSubscribedUser));
                this.updateState({ selectedFields: [...this.state.selectedFields, ...preparedNewFields] });
            }
        }

        return true;
    }

    updateState = (state, callback) => {
        if (this.isComponentMounted) {
            this.setState(state, callback);
        }
    }

    getDefaultSort = (sortItem = '') => {
        const objSort = (this.props.sortList || []).find(sort => sort.name == sortItem);

        if (isEmpty(objSort)) {
            return;
        }

        const label = objSort.name;
        const value = `${objSort.field}${ALLOWED_QUERY_OPERATOR.equal}${objSort.order}`;
        const selectedSort = { label, value };

        return selectedSort;
    }

    /**
     * Method to get the user data export quota
     */
    getUserDataExportQuota = () => this.applyDataExportQuota(userApi.getDataExportQuota(this.props.user.user_id));

    /**
     * Method to set the quota of the popup's data set type from a quota request (its own, or its parent's when it is embedded)
     * @param {Promise} quotaRequest            userApi.getDataExportQuota request
     */
    applyDataExportQuota = (quotaRequest) => {
        const { dataSetType, isClientDirectory, embedded } = this.props;

        return quotaRequest.then(
            response => {
                // an embedded popup keeps the latest of its parent's requests only
                if (embedded && quotaRequest !== this.props.quotaRequest) {
                    return;
                }

                let dataExportQuota = {}, fundingDataExportQuota = {};
                // client directory exports only ever use the CUSTOM quota bucket - no falling back to the
                // entity-type quota, so an org without a CUSTOM row provisioned can't export at all
                let isCustomDataExport = isClientDirectory && response.quota.some(obj => obj.data_set_type === DATA_SET_TYPES.custom);
                let strDataSetType = isClientDirectory ? DATA_SET_TYPES.custom : dataSetType;

                dataExportQuota = response.quota.filter(quota => quota.data_set_type == strDataSetType)[0];

                fundingDataExportQuota = response.quota.filter(quota => quota.data_set_type == "FUNDING")[0];

                // get the number of days until quota reset
                const today = new Date();
                const resetDate = new Date(response.cycle_reset_date);
                const timeDiff = Math.abs(resetDate - today);
                const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

                this.updateState({
                    period: response.period == 3 ? 'month' : 'year',
                    daysUntilReset: daysDiff,
                    dataExportQuota,
                    fundingDataExportQuota,
                    isGeneralQuota: response.is_general_quota,
                    numberOfRecords: dataExportQuota?.number_of_records,
                    isSubscribed: true,
                    isCustomDataExport
                });
            }
        ).catch(error => {
            // an embedded popup's parent logs the error of its request once, for all the views
            if (!embedded) {
                console.error('Failed to load the data export quota', error);
            }
            this.updateState({ isSubscribed: false });
        });
    }

    /**
     * Method to compute a field's allowAccess/disabled flags in place
     */
    prepFieldAccess = (field, user, isSubscribedUser) => {
        // paid field with special access
        if (!field.freeAccess && field.specialAccess) {
            if (user.roles.includes(field.specialAccess)) {
                field.disabled = false;
                field.allowAccess = true;
            } else {
                field.disabled = true;
                field.allowAccess = false;
            }
        }
        // free or default field
        else if (field.freeAccess || field.default) {
            field.allowAccess = true;
        }
        // paid field with no special access - depends on the user's subscription
        else {
            field.allowAccess = !!isSubscribedUser;
            field.disabled = !isSubscribedUser;
        }
        return field;
    }

    /**
     * Method to prepare the list of allowed columns
     */
    prepSelectedFields = () => {
        const { selectableFields, user, dataSetType, isSubscribedUser, exportNameField } = this.props;
        // Deep clone to prevent field select/deselect for export changing the actual table columns
        const initialFields = cloneDeep(selectableFields);

        // add the default name field to the list
        let keyName = "name";
        if (dataSetType === DATA_SET_TYPES.funding) {
            // set the funding name field
            keyName = "investedin_data";
        }

        // a directory view whose first column is not the entity name (the LP of the Commitments view) names its own
        let nameField = [{ fieldName: "Name", keyName, checked: true, disabled: true, ...exportNameField }];

        (initialFields || []).map((field) => this.prepFieldAccess(field, user, isSubscribedUser));

        const arrFields = [...nameField, ...initialFields];

        return arrFields;
    }

    /**
     * Method to prepare the selected user filters with the default directory filters
     */
    prepSelectedFilters = () => {
        const { numberOfRecords } = this.state;
        const { directory, selectedEntities, dataSetType } = this.props;

        // get the user selected filters
        const selectedFilters = this.props.getFilters();

        // get and add the directory default filters to the user's filters
        let filters = '';
        if (ENTITY_DIRECTORIES.includes(directory)) {
            // get only the selected startups' records
            if (selectedEntities && selectedEntities.length > 0) {

                let arrIds = selectedEntities;
                if (selectedEntities.length > numberOfRecords && numberOfRecords != 0) {
                    arrIds = selectedEntities.slice(0, numberOfRecords);
                }

                // the investor_summary rows (the /investors-and-funds INVESTOR view and the LP directory's LPs) are ticked by "<ENTITY_TYPE>-<id>" (an
                // id alone can be an investor's and a corporate's or an enabler's), and be-core matches them by type and id with selected_investor_filter
                const selectionKey = [DATA_SET_TYPES.investor, DATA_SET_TYPES.lps].includes(dataSetType) ? 'selected_investor_filter' : 'id';
                const arrFilters = [
                    { key: selectionKey, value: arrIds, op: ALLOWED_QUERY_OPERATOR.equal }
                ];

                const permanentFilters = prepareFilterParam(arrFilters);

                filters = `${selectedFilters}^,${permanentFilters}`;
            } else {
                filters = selectedFilters;
            }
        } else if (directory == 'Funding') {
            const arrFilters = [
                // get only startups funding rounds
                { key: 'investedin_type_filter', value: ['startup'], op: ALLOWED_QUERY_OPERATOR.equal }
            ];

            if (selectedFilters.indexOf('funding_stage_filter') === -1) {
                // exclude acquisition stage
                arrFilters.push({ key: 'funding_stage_filter', value: [FUNDING_STAGES.acquisition], op: ALLOWED_QUERY_OPERATOR.notEq });
            }

            if (selectedFilters.indexOf('verification_status') === -1) {
                // exclude draft funding rounds
                arrFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq })
            }

            const permanentFilters = prepareFilterParam(arrFilters);

            filters = `${selectedFilters}^,${permanentFilters}`;
        } else if (directory == 'Exits') {
            const arrFilters = [
                // get only acquisition stage funding rounds
                { key: 'funding_stage_filter', value: [FUNDING_STAGES.acquisition], op: ALLOWED_QUERY_OPERATOR.equal },
            ];

            if (filters.indexOf('verification_status') === -1) {
                // exclude draft funding rounds
                arrFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq })
            }

            const permanentFilters = prepareFilterParam(arrFilters);

            filters = `${selectedFilters}^,${permanentFilters}`;
        } else if (directory == 'Funds') {
            const arrFilters = [];

            if (filters.indexOf('verification_status_filter') === -1) {
                // exclude draft funding rounds
                arrFilters.push({ key: 'verification_status_filter', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq })
            }

            const permanentFilters = prepareFilterParam(arrFilters);

            filters = `${selectedFilters}^,${permanentFilters}`;
        } else {
            filters = selectedFilters;
        }

        return filters;
    }

    /**
     * Method to get the selected user sort
     */
    prepSelectedSort = () => {
        const selectedSort = this.props.getSort();
        return selectedSort;
    }

    /**
     * Method to create the user data export request
     */
    handleSubmit = () => {
        this.updateState({
            isProcessing: true,
            errorMessage: ''
        });

        const { user, directory, setState } = this.props;
        const payload = this.buildExportPayload();

        userApi.postDataExportRequest(user.user_id, payload).then(
            response => {
                this.updateState({
                    isProcessing: false,
                    numberOfRecords: 0,
                    sort: ""
                }, () => {
                    // get the new data export quota
                    this.getUserDataExportQuota();
                    this.handleClosePopup();
                    this.props.showSuccessPopup('Data export request has been processed successfully, please check your email.')
                });

                // reset list of selected entities to be exported (a view of a multi-view export passes no setState: its page keeps the ticks)
                if (setState && ENTITY_DIRECTORIES.includes(directory)) {
                    setState({ arrSelectedEntities: [] });
                }
            }
        ).catch(
            error => this.handleError(error)
        );
    }

    /**
     * Methods of an embedded popup (one view of a multi-view export): the parent sends the request of all its views at once, so it asks each
     * view for its request (the records, fields, filters and sort it is set to), and tells it when the request was sent.
     */
    getExportPayload = () => this.buildExportPayload();

    // whether the view sends one of these fields (keyNames): a checked column (only those are in its request). The parent shows a refused
    // field's error in the view it is about.
    sendsAnyField = (keyNames = []) => this.state.selectedFields.some(({ keyName, checked }) => checked && keyNames.includes(keyName));

    // the parent then asks for the quota again, once for all its views (a new quotaRequest)
    handleExportSent = () => {
        this.updateState({ isProcessing: false, numberOfRecords: 0, sort: "", errorMessage: "", errorFields: "" });
    }

    /**
     * Method to prepare the export request of the popup: the records, fields, filters and sort it is set to
     */
    buildExportPayload = () => {
        const { numberOfRecords, selectedFields, selectedFundingFields, sort, isCustomDataExport } = this.state;
        const { recordsCount, selectedEntities, dataSetType } = this.props;

        // get the requested filters
        const selectedFilters = this.prepSelectedFilters();

        // get the number of records to export
        let numberOfRecordsToExport = recordsCount;
        if (selectedEntities && selectedEntities.length > 0) {
            if (selectedEntities.length > numberOfRecords) {
                numberOfRecordsToExport = numberOfRecords;
            } else {
                numberOfRecordsToExport = selectedEntities.length;
            }
        } else if (numberOfRecords && (numberOfRecords < recordsCount)) {
            numberOfRecordsToExport = numberOfRecords;
        }

        // get the requested fields
        let selectedFieldKeys = selectedFields.map(field => {
            if (field.checked) {
                return field.keyName;
            }
        });

        selectedFieldKeys = selectedFieldKeys.filter(Boolean);

        if (dataSetType === DATA_SET_TYPES.startups || dataSetType === DATA_SET_TYPES.privateEquity || dataSetType === DATA_SET_TYPES.company) {
            selectedFundingFields.forEach(fundingField => {
                if (fundingField.checked) {
                    selectedFieldKeys.push(fundingField.keyName)
                }
            })
        }

        // get the requested sort
        const selectedSort = this.prepSelectedSort();
        let sortBy = selectedSort;
        if (sort) {
            sortBy = sort.value;
        }

        const payload = {
            data_set_type: isCustomDataExport ? DATA_SET_TYPES.custom : dataSetType,
            number_of_records: numberOfRecordsToExport,
            fields: selectedFieldKeys,
            filters: selectedFilters,
            sort: sortBy
        };

        return payload;
    }

    /**
     * Method to create an export request entry
     */
    handleSubmitRequest = () => {
        this.updateState({
            isProcessing: true,
            errorMessage: ''
        });

        const { numberOfRecords } = this.state;
        const { user, recordsCount, selectedEntities, dataSetType } = this.props;

        // get the requested filters
        const selectedFilters = this.prepSelectedFilters();

        // get the number of records to export
        let numberOfRecordsToExport = recordsCount;
        if (selectedEntities && selectedEntities.length > 0) {
            numberOfRecordsToExport = selectedEntities.length;
        } else if (numberOfRecords && (numberOfRecords < recordsCount)) {
            numberOfRecordsToExport = numberOfRecords;
        }

        const payload = {
            entity_type: dataSetType,
            data_lines: numberOfRecordsToExport,
            user_id: user.user_id,
            filters: selectedFilters
        };

        exportRequestApi.createExportRequest(payload).then(
            response => {
                this.updateState({
                    isProcessing: false,
                    numberOfRecords: 0,
                    sort: ""
                }, () => {
                    this.handleClosePopup();
                    this.props.showSuccessPopup([
                        "The data export request has been sent, your Account Manager will contact you soon.",
                        <p>For any inquiries, feel free to <a className="red" href="/contactus" target="_blank" rel="noopener noreferrer"><u>contact us</u></a>.</p>
                    ]);
                });
            }
        ).catch(
            error => this.handleError(error)
        );
    }

    /**
     * Method to get the message of a data export request error (never the SQL one)
     */
    getErrorMessage = (error = {}) => {
        // default error message
        let message = 'Something went wrong! Please try again.';

        if (error.response && error.response.data && !isEmpty(error.response.data.errors) && error.response.data.errors[0] && error.response.data.errors[0].message) {
            const strTemp = error.response.data.errors[0].message;

            // don't show the SQL error message
            if (strTemp != "Sequelize DB error.") {
                message = strTemp;
            }
        }

        return message;
    }

    handleError = (error = {}) => {
        const message = this.getErrorMessage(error);

        // the failed fields of an earlier error do not stay with this one
        this.updateState({ errorFields: '' });

        // Append the failed fields with message
        if (error.response && error.response.data && error.response.data.errors && error.response.data.errors[0] && error.response.data.errors[0].value && error.response.data.errors[0].value.length > 0) {
            let errFields = [];
            let value = (error?.response?.data?.errors && error.response.data.errors.length > 0) ? error.response.data.errors[0].value : undefined;

            // Ensure value is an array before calling map
            if (value?.length > 0) {
                value.map(valueItem => {
                    // disallowed fields can come from either the column list or the funding/exits checkbox (e.g. funding_data)
                    const matchedField = this.state.selectedFields.find(item => item.keyName === valueItem)
                        || this.state.selectedFundingFields.find(item => item.keyName === valueItem);

                    errFields.push(matchedField ? matchedField.fieldName : valueItem);
                })
                this.updateState({
                    errorFields: errFields.join(", ")
                });
            }
        }

        this.updateState({
            errorMessage: message,
            isProcessing: false
        });
    }

    handleClosePopup = () => {
        unlockBodyScroll();
        this.updateState({ isOpen: false, isVisible: false });
        this.handleResetFields();
    }

    handleResetFields = () => {
        // Remove funding rounds export on close popup
        let selectedFundingFields = [...this.state.selectedFundingFields];
        selectedFundingFields[0] = { ...selectedFundingFields[0], checked: false };

        this.updateState({
            // options
            isProcessing: false,
            errorMessage: "",
            // funding quota
            selectedFundingFields,
            showFundingQuota: false,
            fundingCountProcessing: false,
            fundingRoundCount: 0
        })
    }

    /**
     * Method to handle the number of records to export
     */
    handleInputChange = (event) => {
        const value = event.target.value;
        let { showFundingQuota } = this.state;

        this.updateState({
            numberOfRecords: value
        }, () => {
            // get funding rounds count for selected startups
            if (showFundingQuota) {
                this.handleFundingRoundCount();
                this.updateState({ fundingCountProcessing: true, fundingMessage: "" })
            } else {
                this.updateState({ fundingRoundCount: 0, fundingMessage: "" })
            }
        });
    }

    /**
     * Method to handle the data sort by
     */
    handleSelectChange = (option) => {
        this.updateState({
            sort: option
        });
    }

    /**
     * Method to handle the select/unselect all
     */
    handleSelectAll = () => {
        const { selectAll, selectedFields } = this.state;

        selectedFields.forEach((field, index) => {
            if (!field.disabled) {
                selectedFields[index].checked = !selectAll;
                this.updateState({ selectedFields });
            }
        });

        this.updateState({
            selectedFields,
            selectAll: !selectAll
        })
    }

    /**
     * Method to handle a field/column select
     */
    handleFieldSelect = (index) => {
        const { selectedFields } = this.state;

        const objTemp = selectedFields[index];

        if (objTemp.allowAccess) {
            selectedFields[index].checked = !objTemp.checked;
            this.updateState({ selectedFields });
        }
    }

    /**
     * Method to handle a funding field select
     */
    handleFundingSelect = (index) => {
        let { selectedFundingFields, showFundingQuota } = this.state;

        const objTemp = selectedFundingFields[index];

        if (objTemp.allowAccess) {
            showFundingQuota = objTemp.checked ? false : true;

            // get funding rounds count for selected startups
            if (showFundingQuota) {
                this.handleFundingRoundCount();
                this.updateState({ fundingCountProcessing: true, fundingMessage: "" })
            } else {
                this.updateState({ fundingRoundCount: 0, fundingMessage: "" })
            }

            selectedFundingFields[index].checked = !objTemp.checked;
            this.updateState({ selectedFundingFields, showFundingQuota });
        }
    }

    /**
     * Method to fetch funding round count
     */
    handleFundingRoundCount = () => {
        const { numberOfRecords, sort, isCustomDataExport } = this.state;
        const { user, recordsCount, selectedEntities, dataSetType } = this.props;

        // get the requested filters
        const selectedFilters = this.prepSelectedFilters();

        // get the number of records to export
        let limit = recordsCount;
        if (selectedEntities && selectedEntities.length > 0) {
            if (selectedEntities.length > numberOfRecords) {
                limit = numberOfRecords;
            } else {
                limit = selectedEntities.length;
            }
        } else if (numberOfRecords && (numberOfRecords < recordsCount)) {
            limit = numberOfRecords;
        }

        // get the requested sort
        const selectedSort = this.prepSelectedSort();
        let sortBy = selectedSort;
        if (sort) {
            sortBy = sort.value;
        }

        userApi.getDataExportFundingCount(
            user.user_id, selectedFilters, sortBy, limit,
            isCustomDataExport ? DATA_SET_TYPES.custom : dataSetType
        ).then(
            response => {
                this.updateState({ fundingRoundCount: response, fundingCountProcessing: false }, () => {
                    this.renderFundingQuotaInfo();
                });
            }
        ).catch(error => {
            this.handleError(error)
            this.updateState({ fundingCountProcessing: false });
        });
    }

    /**
     * Method to render the user funding quota info
     */
    renderFundingQuotaInfo = () => {
        let { dataExportQuota, fundingDataExportQuota, period, daysUntilReset, fundingRoundCount, fundingMessage, isGeneralQuota, isCustomDataExport } = this.state;
        // CUSTOM directory funding draws from the same CUSTOM quota; default to 0 so a missing FUNDING row reads as exhausted, not undefined
        let allowedNumberOfRecords = (isCustomDataExport ? dataExportQuota?.number_of_records : fundingDataExportQuota?.number_of_records) ?? 0;

        // CUSTOM quota is unlimited, so skip the exhausted check here
        if (!isCustomDataExport && allowedNumberOfRecords <= 0) {
            fundingMessage = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You’ve run out of quota for this <span className='red'><strong>{period}</strong></span> for <span className='red'><strong>funding records</strong></span>.{' '}
                    Your quota will refresh in <span className='red'><strong>{daysUntilReset} days</strong></span>. Alternatively you can top up your quota by{' '}
                    <a className='red' href="/contactus" target="_blank" rel="noopener noreferrer">
                        <strong><u>contacting us</u></strong>
                    </a>.
                </p>
            )
        } else if (fundingRoundCount <= 0) {
            fundingMessage = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    No <span className='red'><strong>funding records</strong></span> were found for the selection.
                </p>
            )
        } else if (isCustomDataExport || allowedNumberOfRecords >= fundingRoundCount) {
            fundingMessage = (
                <p className='bd-highlight'>
                    <i className="fas fa-info-circle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You are about to export <span className='red'><strong>{fundingRoundCount} {fundingRoundCount == 1 ? 'line' : 'lines'}</strong></span> of{' '}
                    <span className='red'><strong>funding records</strong></span>.{' '}
                </p>
            )
        } else if (fundingRoundCount > allowedNumberOfRecords) {
            fundingMessage = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You currently only have <span className='red'><strong>{allowedNumberOfRecords} {allowedNumberOfRecords == 1 ? 'line' : 'lines'}</strong></span> of{' '}
                    <span className='red'><strong>funding records</strong></span> left for this <span className='red'><strong>{period}</strong></span>.{' '}
                    The selected filters result in <span className='red'><strong>{fundingRoundCount} {fundingRoundCount == 1 ? 'line' : 'lines'}</strong></span> of data.{' '}
                    You can update the filters or you can top up your quota by{' '}
                    <a className='red' href="/contactus" target="_blank" rel="noopener noreferrer">
                        <strong><u>contacting us</u></strong>
                    </a>.
                </p>
            )
        }

        this.updateState({ fundingMessage });
    }

    /**
     * Method to render the user quota info
     */
    renderQuotaInfo = () => {
        const { dataExportQuota, numberOfRecords, period, daysUntilReset, showFundingQuota, fundingRoundCount, isCustomDataExport } = this.state;
        const { recordsCount, selectedEntities } = this.props;

        const dataSetType = dataExportQuota?.data_set_type;
        const allowedNumberOfRecords = dataExportQuota?.number_of_records;
        const showFundingCount = showFundingQuota && fundingRoundCount;

        let type = '';
        if (dataSetType == DATA_SET_TYPES.startups) {
            type = 'startups';
        } else if (dataSetType == DATA_SET_TYPES.ecosystem || dataSetType == DATA_SET_TYPES.investor) {
            type = 'investors';
        } else if (dataSetType == DATA_SET_TYPES.lps) {
            type = 'LPs';
        } else if (dataSetType == DATA_SET_TYPES.lpCommitments) {
            type = 'commitments';
        } else if (dataSetType == DATA_SET_TYPES.investorFunds) {
            type = 'funds';
        } else if (dataSetType == DATA_SET_TYPES.investorInvestments) {
            type = 'investments';
        } else if (dataSetType == DATA_SET_TYPES.funding) {
            type = 'funding records';
        } else if (dataSetType == DATA_SET_TYPES.funds) {
            type = 'funds';
        } else if (dataSetType == DATA_SET_TYPES.privateEquity) {
            type = 'private equity';
        } else if (dataSetType === DATA_SET_TYPES.company) {
            type = 'companies';
        } else if (dataSetType === DATA_SET_TYPES.custom) {
            type = 'custom';
        }

        let message = '';
        // case1: user has unlimited access (CUSTOM directory quota)
        // case2: user consumed quota
        // case3: filters result in zero records
        // case4: user selects which startups to export
        // case5: user inputs number of records to be exported
        // case6: filtered data number is bigger than the available user's quota
        // case7: filtered data number is less than the available user's quota
        if (isCustomDataExport) {
            message = (
                <p className='bd-highlight'>
                    <i className="fas fa-info-circle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You have unlimited quota.{' '}
                    Please select the fields you would like in your export and the metric for top <span className='red'><strong>{type}</strong></span>.
                </p>
            )
        } else if (allowedNumberOfRecords == 0) {
            message = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You’ve run out of quota for this <span className='red'><strong>{period}</strong></span> for <span className='red'><strong>{type}</strong></span>.{' '}
                    Your quota will refresh in <span className='red'><strong>{daysUntilReset} days</strong></span>. Alternatively you can top up your quota by{' '}
                    <a className='red' href="/contactus" target="_blank" rel="noopener noreferrer">
                        <strong><u>contacting us</u></strong>
                    </a>.
                </p>
            )
        } else if (recordsCount === 0) {
            message = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    No results were found for the selected filters. Please go back and update the filters.
                </p>
            )
        } else if (selectedEntities && (selectedEntities.length > 0) && (allowedNumberOfRecords >= selectedEntities.length)) {
            const exportLines = (showFundingCount && (fundingRoundCount > selectedEntities.length)) ? fundingRoundCount : (recordsCount > selectedEntities.length) ? selectedEntities.length : recordsCount;

            message = (
                <p className='bd-highlight'>
                    <i className="fas fa-info-circle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You are about to export <span className='red'><strong>{exportLines} lines</strong></span> of{' '}
                    {showFundingCount ?
                        <span className='red'><strong>data</strong>{' '}</span>
                        :
                        <>
                            <span className='red'><strong>{type}</strong></span> out of <span className='red'><strong>{recordsCount}</strong></span>{' '}
                        </>
                    }
                    based on your selection.{' '}
                    Your remaining quota after this export will be <span className='red'><strong>{allowedNumberOfRecords - exportLines} lines</strong></span>.{' '}
                    Please select the fields you would like in your export and the metric for top <span className='red'><strong>{type}</strong></span>.
                </p>
            )
        } else if ((numberOfRecords > 0) && (allowedNumberOfRecords >= numberOfRecords)) {
            const exportLines = (showFundingCount && (fundingRoundCount > numberOfRecords)) ? fundingRoundCount : (recordsCount > numberOfRecords) ? numberOfRecords : recordsCount;

            message = (
                <p className='bd-highlight'>
                    <i className="fas fa-info-circle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You are about to export the top <span className='red'><strong>{exportLines} lines</strong></span> of{' '}
                    {showFundingCount ?
                        <span className='red'><strong>data</strong>{' '}</span>
                        :
                        <>
                            <span className='red'><strong>{type}</strong></span> out of <span className='red'><strong>{recordsCount}</strong></span>{' '}
                        </>
                    }
                    based on the sort selected.{' '}
                    Your remaining quota after this export will be <span className='red'><strong>{allowedNumberOfRecords - exportLines} lines</strong></span>.{' '}
                    Please select the fields you would like in your export and the metric for top <span className='red'><strong>{type}</strong></span>.
                </p>
            )
        } else if (recordsCount > allowedNumberOfRecords) {
            message = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You currently only have <span className='red'><strong>{allowedNumberOfRecords} lines</strong></span> of{' '}
                    <span className='red'><strong>{type}</strong></span> left for this <span className='red'><strong>{period}</strong></span>.{' '}
                    The selected filters result in <span className='red'><strong>{recordsCount} lines</strong></span> of data.{' '}
                    You can select up to <span className='red'><strong>{allowedNumberOfRecords}</strong></span>{' '}
                    <span className='red'><strong>{type}</strong></span> or you can top up your quota by{' '}
                    <a className='red' href="/contactus" target="_blank" rel="noopener noreferrer">
                        <strong><u>contacting us</u></strong>
                    </a>.
                </p>
            )
        } else if (!dataSetType) {
            const { dataSetType } = this.props;
            const directoryName = dataSetType.charAt(0).toUpperCase() + dataSetType.slice(1).toLowerCase();

            message = (
                <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                    <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                    Exports from {directoryName} directory are not included in your subscription. Please {' '}
                    <a className='red' href="/contactus" target="_blank" rel="noopener noreferrer">
                        <strong><u>contact us</u></strong>
                    </a>. for more details.{' '}
                </p>
            )
        } else {
            let exportLines = showFundingCount ? fundingRoundCount : recordsCount;

            message = (
                <p className='bd-highlight'>
                    <i className="fas fa-info-circle red pr-1" style={{ fontSize: '1rem' }}></i>
                    You are about to export <span className='red'><strong>{exportLines} lines</strong></span> of{' '}
                    {(showFundingQuota && fundingRoundCount) ?
                        <span className='red'><strong>data</strong>{' '}</span>
                        :
                        <>
                            <span className='red'><strong>{type}</strong></span> out of <span className='red'><strong>{recordsCount}</strong></span>{' '}
                        </>
                    }
                    <span className='red'><strong>{allowedNumberOfRecords - exportLines} lines</strong></span>.{' '}
                    Please select the fields you would like in your export.
                </p>
            )
        }

        return message;
    }

    /**
     * Method to render the number of records input field
     */
    renderRecordsField = () => {
        const { numberOfRecords } = this.state;
        const { selectedEntities } = this.props;

        let isDisabled = false;
        let records = numberOfRecords;

        if (selectedEntities && selectedEntities.length > 0 && selectedEntities.length > numberOfRecords) {
            isDisabled = true;
            records = numberOfRecords;
        } else if (selectedEntities && selectedEntities.length > 0) {
            isDisabled = true;
            records = selectedEntities.length;
        }

        const inputField = <div className="col-sm-6 col-md-4">
            <div className="d-flex flex-column align-items-start">
                <label><strong>Number of records to export:</strong></label>
                <input
                    type="number"
                    name="numberOfRecords"
                    placeholder="e.g. 10 records"
                    onChange={this.handleInputChange}
                    value={records}
                    disabled={isDisabled}
                    min="0"
                    style={{
                        height: 30,
                        borderRadius: 5,
                        backgroundColor: '#FFF',
                        paddingLeft: 15,
                        borderColor: "#bebebe"
                    }}
                />
            </div>
        </div>;

        return inputField;
    }

    /**
     * Method to render the sort select field
     */
    renderSortField = () => {
        const { sort } = this.state;
        const { sortList } = this.props;

        const sortOptions = (sortList || []).map(sortItem => {
            const label = sortItem.name;
            const value = `${sortItem.field}${ALLOWED_QUERY_OPERATOR.equal}${sortItem.order}`;

            return { label, value };
        });

        const sortField = <div className="col-sm-6 col-md-4">
            <label><strong>Sort data by:</strong></label>
            <Select placeholder="Sort data by"
                theme={reactSelectTheme}
                styles={{ ...reactSelectSmall, menu: provided => ({ ...provided, zIndex: 9999 }) }}
                options={sortOptions}
                value={sort}
                onChange={this.handleSelectChange}
            />
        </div>;

        return sortField;
    }

    /**
     * Method to render the columns grid
     */
    renderColumns = () => {
        const { selectedFields, selectAll } = this.state;
        // the ids of the inputs are unique when several popups are on the page (the views of MultiViewDataExportPopup)
        const idPrefix = this.props.idPrefix || '';

        return <div className="container fields-grid">
            <div><label><strong>Selected Columns:</strong></label></div>

            <div className="custom-control custom-checkbox mb-3">
                <input id={`${idPrefix}select-all`} type="checkbox" className="custom-control-input" checked={selectAll} onChange={() => this.handleSelectAll()} />
                <label htmlFor={`${idPrefix}select-all`} className="custom-control-label">{selectAll ? 'Deselect' : 'Select'} All Columns</label>
            </div>

            <div className="row m-0">
                {selectedFields.map((field, index) => {
                    return (
                        <div className="col-xl-4 custom-control custom-checkbox" key={index}>
                            <input id={`${idPrefix}data-export${index}`} type="checkbox" className="custom-control-input" disabled={field.disabled} checked={field.checked} onChange={() => this.handleFieldSelect(index)} />
                            <label htmlFor={`${idPrefix}data-export${index}`} className="custom-control-label">
                                {field.fieldName}
                            </label>
                        </div>
                    )
                })}
            </div>
        </div>;
    }

    /**
     * Method to render the columns grid
     */
    renderFundingColumns = () => {
        const { dataSetType } = this.props;
        const { selectedFundingFields, showFundingQuota, fundingMessage, fundingCountProcessing } = this.state;

        return <div className="container fields-grid">
            <label>
                <strong>{dataSetType == DATA_SET_TYPES.privateEquity ? "Include Transactions"
                    : dataSetType == DATA_SET_TYPES.company ? "Include Transactions and Exits for Companies"
                        : "Include Funding Rounds and Exits for Startups"}:</strong>
            </label>
            <i className="far fa-question-circle pl-1" data-toggle="tooltip" data-placement="top" title="This will be included in your export as a separate tab"></i>

            {(showFundingQuota && fundingMessage) &&
                <div className="row pl-3 pt-1 pr-3 pb-3">
                    {fundingCountProcessing &&
                        <i className="fas fa-circle-notch fa-spin mr-2"></i>
                    }
                    {fundingMessage}
                </div>
            }

            <div className="row m-0">
                {selectedFundingFields.map((field, index) => {
                    return (
                        <div className="col-xl-4 custom-control custom-checkbox" key={index}>
                            <input id={"data-export-funding" + index} type="checkbox" className="custom-control-input" disabled={field.disabled} checked={field.checked} onChange={() => this.handleFundingSelect(index)} />
                            <label htmlFor={"data-export-funding" + index} className="custom-control-label">
                                {field.fieldName}
                            </label>
                        </div>
                    )
                })}
            </div>
        </div>;
    }

    /**
     * Method to render the submit button
     */
    renderSubmitButton = () => {
        const mode = this.getSubmitMode();

        // the Send button of each mode (see getSubmitMode)
        if (mode === 'processing') {
            return <button type="button" className="btn btn-main" disabled>
                <i className="fas fa-circle-notch fa-spin mr-2"></i>
                Processing
            </button>;
        } else if (mode === 'contact-sales') {
            return <a type="button" className="btn btn-secondary" href="/pricing" target="_blank" rel="noopener noreferrer">
                Contact Sales
            </a>;
        } else if (mode === 'contact-us') {
            return <a type="button" className="btn btn-secondary" href="/contactus" target="_blank" rel="noopener noreferrer">
                Contact Us
            </a>;
        } else if (mode === 'send') {
            return <button type="button" className="btn btn-main" onClick={this.handleSubmit}>
                Send Request
            </button>;
        } else if (mode === 'request') {
            return <button type="button" className="btn btn-main" onClick={this.handleSubmitRequest}>
                Send Request
            </button>;
        }

        return <button type="button" className="btn btn-main" disabled>
            Send Request
        </button>;
    }

    /**
     * Method to get what the submit button is: 'processing', 'contact-sales', 'contact-us', 'send' (the export can be sent), 'request' (the quota
     * is used up: an export request entry for the account manager) or 'disabled'
     */
    getSubmitMode = () => {
        const { isSubscribedUser, recordsCount, dataSetType, isClientDirectory } = this.props;
        const { dataExportQuota, fundingDataExportQuota, isProcessing, numberOfRecords, isSubscribed, isGeneralQuota, selectedFundingFields, isCustomDataExport } = this.state;

        const allowedNumberOfRecords = dataExportQuota?.number_of_records;

        // CUSTOM directory funding draws from the same CUSTOM quota; default to 0 so a missing FUNDING row reads as exhausted, not undefined
        const allowedFundingRecords = (isCustomDataExport ? dataExportQuota?.number_of_records : fundingDataExportQuota?.number_of_records) ?? 0;
        const isFundingSelected = selectedFundingFields[0]?.checked;
        const fundingQuotaExhausted = isFundingSelected && allowedFundingRecords <= 0;

        // case1: request is being processed
        // case2: user is not subscribed
        // case3: Private equity directory export without general quota
        // case4: client directory export without a CUSTOM quota provisioned
        // case5: filters result in zero records
        // case6: user has data quota but selected funding/exits export while out of funding quota
        // case7: filtered data number is bigger than the available user's quota
        // case8: user inputs number of records to be exported
        if (isProcessing) {
            return 'processing';
        } else if (!isSubscribedUser || !isSubscribed || !dataSetType) {
            return 'contact-sales';
        } else if (dataSetType == DATA_SET_TYPES.privateEquity && !isGeneralQuota) {
            return 'contact-us';
        } else if (isClientDirectory && !isCustomDataExport) {
            return 'contact-us';
        } else if (recordsCount === 0) {
            return 'disabled';
        } else if (allowedNumberOfRecords > 0 && fundingQuotaExhausted) {
            return 'disabled';
        } else if (allowedNumberOfRecords > recordsCount) {
            return 'send';
        } else if ((numberOfRecords > 0) && (allowedNumberOfRecords >= numberOfRecords)) {
            return 'send';
        } else if (allowedNumberOfRecords === 0) {
            return 'request';
        }

        return 'disabled';
    }

    /**
     * Method to render the body of the popup: the quota info, the records and sort fields, the columns and the error message
     */
    renderBody() {
        const { dataSetType, isClientDirectory } = this.props;
        const isBlockedClientDirectory = isClientDirectory && !this.state.isCustomDataExport;

        return (
            <>
                {this.state.isSubscribed ?
                    <>
                        {(isBlockedClientDirectory || (dataSetType == DATA_SET_TYPES.privateEquity && !this.state.isGeneralQuota)) ?
                            <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                                <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                            You don't have access to export data from this directory. Please reach out to <a href="mailto:support@magnitt.com" className="red">support@magnitt.com</a> for assistance.
                            </p>
                            :
                            <>
                                <div>
                                    {this.renderQuotaInfo()}
                                </div>
                                <hr />
                                <div className="row">
                                    {this.renderRecordsField()}
                                    {this.renderSortField()}
                                </div>
                                <hr />
                                {this.renderColumns()}
                                {(dataSetType === DATA_SET_TYPES.startups || dataSetType === DATA_SET_TYPES.privateEquity || dataSetType === DATA_SET_TYPES.company) &&
                                <>
                                    <hr />
                                    {this.renderFundingColumns()}
                                </>
                                }
                            </>
                        }
                    </>
                    :
                    <div className="pl-3 pr-3">
                        <p className='bd-highlight' style={{ padding: 10, backgroundColor: "#f6cfda" }}>
                            <i className="fas fa-exclamation-triangle red pr-1" style={{ fontSize: '1rem' }}></i>
                        The data export feature is only available to our subscribed users.
                        You can look into our <a className='red' href="/pricing" target="_blank" rel="noopener noreferrer"><strong><u>different plans</u></strong></a>{' '}
                        or you can contact sales to tailor a package to suit your needs.
                        </p>
                    </div>
                }

                {this.state.errorMessage &&
                <>
                    <hr />
                    <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle red"></i>
                        <p className="pl-2 red">
                            {this.state.errorMessage}
                            {this.state.errorFields &&
                                <>: {this.state.errorFields}</>
                            }
                        </p>
                    </div>
                </>
                }
            </>
        );
    }

    render() {
        // an embedded popup is one view of a multi-view export: the modal and its Send button are the parent's
        if (this.props.embedded) {
            return <div className="data-export-embedded">{this.renderBody()}</div>;
        }

        const { isOpen, isVisible, isProcessing } = this.state;

        return (
            <DataExportModal isOpen={isOpen} isVisible={isVisible} isProcessing={isProcessing} onClose={this.handleClosePopup} footer={this.renderSubmitButton()}>
                {this.renderBody()}
            </DataExportModal>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.userReducer,
});

export default connect(mapStateToProps)(DataExportPopup);
